import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, getDocs, doc, deleteDoc, updateDoc, query, orderBy } from "firebase/firestore";
import { Trash2, Ban, CheckCircle, X, Package, User, ShieldAlert, Loader2, MessageSquare, Truck, DollarSign, Crown, Calendar } from 'lucide-react';
import { PLANS, activateSubscription, cancelSubscription, hasActiveSubscription, daysLeft } from './subscriptionService';

export default function AdminPanel({ onClose }) {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAllData(); }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const usersSnap = await getDocs(collection(db, "users"));
      setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const delQuery = query(collection(db, "deliveries"), orderBy("requestedAt", "desc"));
      const delSnap = await getDocs(delQuery);
      setDeliveries(delSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const msgsQuery = query(collection(db, "contact_requests"), orderBy("createdAt", "desc"));
      const msgsSnap = await getDocs(msgsQuery);
      setMessages(msgsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  // --- ניהול משתמשים ---
  const handleDeleteUser = async (userId) => {
    if (!window.confirm("האם למחוק את המשתמש לצמיתות?")) return;
    try {
      await deleteDoc(doc(db, "users", userId));
      setUsers(users.filter(u => u.id !== userId));
    } catch (e) { alert("שגיאה במחיקה"); }
  };

  const handleBlockUser = async (userId, currentStatus) => {
    try {
      await updateDoc(doc(db, "users", userId), { isBlocked: !currentStatus });
      setUsers(users.map(u => u.id === userId ? { ...u, isBlocked: !currentStatus } : u));
    } catch (e) { alert("שגיאה בחסימה"); }
  };

  const handleVerifyCourier = async (userId) => {
    if (!window.confirm("לאשר את השליח (רישיון נהיגה תקין)?")) return;
    try {
      await updateDoc(doc(db, "users", userId), { isCourierVerified: true });
      setUsers(users.map(u => u.id === userId ? { ...u, isCourierVerified: true } : u));
    } catch (e) { alert("שגיאה באישור"); }
  };

  // --- ניהול מנויים ---
  const handleActivateSub = async (user, planKey) => {
    const plan = PLANS[planKey];
    if (!window.confirm(`להפעיל מנוי ${plan.label} (${plan.price}₪) ל-${user.fullName}?`)) return;
    try {
      const until = await activateSubscription(user.id, planKey, user.subscriptionUntil);
      setUsers(users.map(u => u.id === user.id
        ? { ...u, subscriptionPlan: planKey, subscriptionUntil: { toDate: () => until } }
        : u));
      alert(`מנוי הופעל עד ${until.toLocaleDateString('he-IL')}`);
    } catch (e) { alert("שגיאה בהפעלת מנוי: " + e.message); }
  };

  const handleCancelSub = async (userId) => {
    if (!window.confirm("לבטל את המנוי?")) return;
    try {
      await cancelSubscription(userId);
      setUsers(users.map(u => u.id === userId ? { ...u, subscriptionPlan: null, subscriptionUntil: null } : u));
    } catch (e) { alert("שגיאה בביטול"); }
  };

  // --- ניהול משלוחים ---
  const handleDeleteDelivery = async (id) => {
    if (!window.confirm("למחוק את המשלוח?")) return;
    try {
      await deleteDoc(doc(db, "deliveries", id));
      setDeliveries(deliveries.filter(d => d.id !== id));
    } catch (e) { alert("שגיאה במחיקה"); }
  };

  // --- הודעות ---
  const handleDeleteMessage = async (msgId) => {
    if (!window.confirm("למחוק את ההודעה?")) return;
    try {
      await deleteDoc(doc(db, "contact_requests", msgId));
      setMessages(messages.filter(m => m.id !== msgId));
    } catch (e) { alert("שגיאה במחיקה"); }
  };

  const handleMarkAsRead = async (msgId) => {
    try {
      await updateDoc(doc(db, "contact_requests", msgId), { status: 'read' });
      setMessages(messages.map(m => m.id === msgId ? { ...m, status: 'read' } : m));
    } catch (e) { console.log(e); }
  };

  // --- חישובי הכנסה (אומדן לפי מנויים פעילים) ---
  const couriers = users.filter(u => u.isCourier || u.courierInfo);
  const activeSubscribers = users.filter(u => hasActiveSubscription(u));
  const estimatedMRR = activeSubscribers.reduce((sum, u) => {
    const plan = PLANS[u.subscriptionPlan];
    if (!plan) return sum;
    // נרמול להכנסה חודשית
    return sum + (plan.price / (plan.days / 30));
  }, 0);

  const STATUS_HE = {
    requested: 'ממתין', accepted: 'נתפס', picked_up: 'בדרך', delivered: 'נמסר', cancelled: 'בוטל',
  };

  if (loading) return <div className="flex justify-center items-center h-full"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="bg-gray-50 min-h-full p-4 text-right" dir="rtl">

      <div className="flex justify-between items-center mb-6">
        <button onClick={onClose} className="bg-white p-2 rounded-full shadow"><X /></button>
        <h1 className="text-2xl font-black flex items-center gap-2"><ShieldAlert /> פאנל ניהול</h1>
      </div>

      {/* כרטיסי סיכום */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl p-4 shadow">
          <div className="text-2xl font-black text-gray-800">{users.length}</div>
          <div className="text-xs text-gray-400">משתמשים</div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow">
          <div className="text-2xl font-black text-blue-600">{couriers.length}</div>
          <div className="text-xs text-gray-400">שליחים</div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow">
          <div className="text-2xl font-black text-green-600">{activeSubscribers.length}</div>
          <div className="text-xs text-gray-400">מנויים פעילים</div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow">
          <div className="text-2xl font-black text-green-700">₪{Math.round(estimatedMRR)}</div>
          <div className="text-xs text-gray-400">הכנסה חודשית (אומדן)</div>
        </div>
      </div>

      {/* לשוניות */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        <button onClick={() => setActiveTab('users')} className={`flex-1 py-3 px-4 rounded-xl font-bold flex justify-center gap-2 whitespace-nowrap ${activeTab === 'users' ? 'bg-black text-white' : 'bg-white text-gray-600'}`}>
          <User size={18} /> משתמשים ({users.length})
        </button>
        <button onClick={() => setActiveTab('couriers')} className={`flex-1 py-3 px-4 rounded-xl font-bold flex justify-center gap-2 whitespace-nowrap ${activeTab === 'couriers' ? 'bg-black text-white' : 'bg-white text-gray-600'}`}>
          <Crown size={18} /> שליחים ומנויים ({couriers.length})
        </button>
        <button onClick={() => setActiveTab('deliveries')} className={`flex-1 py-3 px-4 rounded-xl font-bold flex justify-center gap-2 whitespace-nowrap ${activeTab === 'deliveries' ? 'bg-black text-white' : 'bg-white text-gray-600'}`}>
          <Package size={18} /> משלוחים ({deliveries.length})
        </button>
        <button onClick={() => setActiveTab('messages')} className={`flex-1 py-3 px-4 rounded-xl font-bold flex justify-center gap-2 whitespace-nowrap ${activeTab === 'messages' ? 'bg-black text-white' : 'bg-white text-gray-600'}`}>
          <MessageSquare size={18} /> הודעות ({messages.length})
        </button>
      </div>

      {/* === משתמשים === */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-xl shadow overflow-hidden overflow-x-auto">
          <table className="w-full text-sm text-right">
            <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-3">שם</th>
                <th className="px-4 py-3">סטטוס</th>
                <th className="px-4 py-3">פעולות</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map(user => (
                <tr key={user.id} className={user.isBlocked ? "bg-red-50" : ""}>
                  <td className="px-4 py-3 font-bold flex items-center gap-2 min-w-[150px]">
                    <img src={user.profileImage || "https://via.placeholder.com/30"} className="w-8 h-8 rounded-full object-cover" />
                    <div>
                      <div>{user.fullName}</div>
                      <div className="text-xs text-gray-400">{user.phone || user.phoneNumber || '—'}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {user.isIdentityVerified ? <span className="text-green-600 font-bold flex items-center gap-1"><CheckCircle size={12} /> מאומת</span> : <span className="text-gray-400">ממתין</span>}
                    {user.isBlocked && <span className="block text-red-600 font-bold text-xs">חסום!</span>}
                  </td>
                  <td className="px-4 py-3 flex gap-2">
                    <button onClick={() => handleBlockUser(user.id, user.isBlocked)} className="bg-orange-100 text-orange-700 p-1.5 rounded hover:bg-orange-200"><Ban size={16} /></button>
                    <button onClick={() => handleDeleteUser(user.id)} className="bg-red-100 text-red-700 p-1.5 rounded hover:bg-red-200"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* === שליחים ומנויים === */}
      {activeTab === 'couriers' && (
        <div className="bg-white rounded-xl shadow overflow-hidden overflow-x-auto">
          <table className="w-full text-sm text-right">
            <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-3">שליח</th>
                <th className="px-4 py-3">אימות</th>
                <th className="px-4 py-3">מנוי</th>
                <th className="px-4 py-3">הפעלת מנוי</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {couriers.length === 0 ? (
                <tr><td colSpan={4} className="p-6 text-center text-gray-400">אין שליחים רשומים עדיין</td></tr>
              ) : couriers.map(user => {
                const active = hasActiveSubscription(user);
                return (
                  <tr key={user.id} className={user.isBlocked ? "bg-red-50" : ""}>
                    <td className="px-4 py-3 font-bold min-w-[140px]">
                      <div className="flex items-center gap-2">
                        <img src={user.profileImage || "https://via.placeholder.com/30"} className="w-8 h-8 rounded-full object-cover" />
                        <div>
                          <div>{user.fullName}</div>
                          <div className="text-xs text-gray-400">{user.courierInfo?.vehicle || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {user.isCourierVerified ? (
                        <span className="text-green-600 font-bold flex items-center gap-1"><CheckCircle size={12} /> רישיון מאומת</span>
                      ) : (
                        <button onClick={() => handleVerifyCourier(user.id)} className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold hover:bg-green-200">אשר רישיון</button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {active ? (
                        <span className="text-green-600 font-bold flex items-center gap-1">
                          <Calendar size={12} /> {daysLeft(user)} ימים
                        </span>
                      ) : (
                        <span className="text-gray-400">אין מנוי</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {Object.entries(PLANS).map(([key, plan]) => (
                          <button key={key} onClick={() => handleActivateSub(user, key)} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-[11px] font-bold hover:bg-blue-100 border border-blue-100">
                            +{plan.label} ₪{plan.price}
                          </button>
                        ))}
                        {active && (
                          <button onClick={() => handleCancelSub(user.id)} className="bg-red-50 text-red-600 px-2 py-1 rounded text-[11px] font-bold hover:bg-red-100">בטל</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* === משלוחים === */}
      {activeTab === 'deliveries' && (
        <div className="bg-white rounded-xl shadow overflow-hidden overflow-x-auto">
          <table className="w-full text-sm text-right">
            <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-3">מסלול</th>
                <th className="px-4 py-3">שולח / שליח</th>
                <th className="px-4 py-3">מחיר</th>
                <th className="px-4 py-3">סטטוס</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {deliveries.length === 0 ? (
                <tr><td colSpan={5} className="p-6 text-center text-gray-400">אין משלוחים עדיין</td></tr>
              ) : deliveries.map(d => (
                <tr key={d.id}>
                  <td className="px-4 py-3 max-w-[200px]">
                    <div className="text-xs text-gray-600 truncate">↑ {d.pickup?.address}</div>
                    <div className="text-xs text-gray-600 truncate">↓ {d.dropoff?.address}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div>{d.senderName}</div>
                    <div className="text-gray-400">{d.courierName || 'טרם נתפס'}</div>
                  </td>
                  <td className="px-4 py-3 font-bold text-blue-700">₪{d.fare}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      d.status === 'delivered' ? 'bg-green-100 text-green-700'
                      : d.status === 'cancelled' ? 'bg-red-100 text-red-600'
                      : 'bg-blue-100 text-blue-700'
                    }`}>{STATUS_HE[d.status] || d.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleDeleteDelivery(d.id)} className="bg-red-100 text-red-700 p-1.5 rounded hover:bg-red-200"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* === הודעות === */}
      {activeTab === 'messages' && (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          {messages.length === 0 ? (
            <div className="p-8 text-center text-gray-500">אין הודעות חדשות</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {messages.map(msg => (
                <div key={msg.id} className={`p-4 hover:bg-gray-50 ${msg.status !== 'read' ? 'bg-blue-50/50' : ''}`}>
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs text-gray-400">
                      {msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleDateString('he-IL') : 'היום'}
                    </span>
                    <div className="flex gap-2">
                      {msg.status !== 'read' && (
                        <button onClick={() => handleMarkAsRead(msg.id)} title="סמן כנקרא" className="text-blue-600 hover:bg-blue-100 p-1 rounded"><CheckCircle size={16} /></button>
                      )}
                      <button onClick={() => handleDeleteMessage(msg.id)} title="מחק" className="text-red-400 hover:bg-red-100 p-1 rounded"><Trash2 size={16} /></button>
                    </div>
                  </div>
                  <div className="font-bold text-gray-800 mb-1">{msg.senderName || msg.userName} <span className="font-normal text-gray-400 text-xs">({msg.senderPhone || msg.userPhone})</span></div>
                  <div className="text-sm text-gray-600 bg-white/50 p-2 rounded border border-gray-100">{msg.content || msg.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}